import { useQuery } from "@apollo/client";
import { addMonths, max, min, parse, subMonths } from "date-fns";
import Head from "next/head";
import { useEffect } from "react";
import { default as validateUuid } from "uuid-validate";
import { useAnalyticsTracker } from "../../../components/Atoms/Analytics";
import { useSession } from "../../../components/Atoms/AppSession";
import Loader from "../../../components/Atoms/Loader";
import Checkout from "../../../components/Templates/Checkout";
import { getInitialState } from "../../../components/Templates/Checkout/getInitialState";
import {
  Product,
  ProductAvailability,
  ProductAvailabilityType,
} from "../../../graphql/operations";
import { CustomError } from "../../../utils/Error";
import { addDuration, formatDate } from "../../../utils/calendarUtils";
import { GET_CHECKOUT_PRODUCT } from "../../../utils/queries/ProductCheckout";
import Custom404 from "../../404";
import Custom500 from "../../500";

declare global {
  interface Window {
    gtag: any;
  }
}

interface QueryProps {
  query: {
    id: string;
    date: string;
    time: string;
    isprivate: boolean;
  };
}

const CheckoutOrder = ({ query }: QueryProps) => {
  const session = useSession();

  const { currencyCode, languageCode, countryCode } = session;

  let now = new Date();

  // from is one month in the past from the query date
  // and clamped between the current date, and 13 months in the future
  const from = min([
    addMonths(now, 13),
    max([
      now,
      subMonths(
        parse(
          (query?.date as string) ?? "1970-01-01",
          "yyyy-MM-dd",
          new Date()
        ),
        1
      ),
    ]),
  ]);

  const until = addDuration(from, { months: 2 });

  const tracker = useAnalyticsTracker();
  useEffect(() => {
    tracker.trackEvent({
      category: "onpage",
      action: "checkout",
      label: "checkout",
    });
  }, [tracker]);

  const { loading, error, data } = useQuery<{
    product: Product;
    productAvailability: ProductAvailability;
  }>(GET_CHECKOUT_PRODUCT, {
    fetchPolicy: "cache-and-network",
    variables: {
      productId: query.id,
      fromDate: formatDate(from),
      untilDate: formatDate(until),
      currency: currencyCode,
      language: languageCode,
    },
  });

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!data || !data.product) {
    return <Custom404 />;
  }

  if (error) {
    return <Custom500 />;
  }

  if (!validateUuid(query.id as string)) {
    const error: CustomError = new Error(`${query.id} is not a valid UUID`);
    error.code = "INVALID_UUID";
    throw error;
  }

  const { product } = data;

  const availability = data.product.availability;
  // fix initial state dumb it down
  const initialState = getInitialState({
    apolloData: {
      ...data,
      product,
    },
    query,
    // add stored state somehow
    contactInformation: {
      names: [
        {
          givenName: "",
          familyName: "",
        },
      ],
      email: null,
      confirmEmail: null,
      contactNumber: null,
      acceptsMarketing: false,
    },
    guideId: null,
    couponCode: null,
  });

  const isPrivate = {
    [ProductAvailabilityType.Public]: false,
    [ProductAvailabilityType.Private]: true,
    [ProductAvailabilityType.PublicAndPrivate]: !!query?.isprivate,
  }[product.availabilityType];

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <meta name="robots" content="noindex" />
        <title>MagicalTrip Checkout</title>
      </Head>
      <Checkout
        initialState={initialState}
        product={product}
        currency={currencyCode}
        language={languageCode}
        countryCode={countryCode}
        availability={availability}
        isPrivate={isPrivate}
      />
    </>
  );
};

export default CheckoutOrder;

export const getServerSideProps = async ({ query }: QueryProps) => {
  return {
    props: {
      query,
    },
  };
};
